// src/App.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useSwipeable } from 'react-swipeable';
import emailjs from '@emailjs/browser';
import 'react-lazy-load-image-component/src/effects/blur.css';
import './App.css';
import SEO from './components/SEO';
import { fetchPhotos, fetchSiteSettings, fetchPackages } from './contentfulClient';

const EMAILJS_SERVICE_ID = 'service_dam787u';
const EMAILJS_TEMPLATE_ID = 'template_1yc7fxk';
const EMAILJS_PUBLIC_KEY = 'cJnEe-Q2EP1oHEQX8';

const categories = [
	{ id: 'all', name: 'All Work', icon: '' },
	{ id: 'food', name: 'Food', icon: '' },
	{ id: 'seniors', name: 'Seniors', icon: '' },
	{ id: 'maternity', name: 'Maternity', icon: '' },
	{ id: 'birthdays', name: 'Birthdays', icon: '' },
	{ id: 'selflove', name: 'Self-Love', icon: '' },
	{ id: 'family', name: 'Family', icon: '' },
	{ id: 'couples', name: 'Couples', icon: '' },
	{ id: 'branding', name: 'Branding', icon: '' },
];

function App() {
	const [activeCategory, setActiveCategory] = useState('all');
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);
	const [galleryData, setGalleryData] = useState({});
	const [siteSettings, setSiteSettings] = useState({});
	const [packages, setPackages] = useState([]);
	const [loading, setLoading] = useState(true);
	const [formData, setFormData] = useState({ name: '', email: '', service: '', message: '' });
	const [formStatus, setFormStatus] = useState('idle'); // idle | sending | success | error

	// Fetch data from Contentful on mount
	useEffect(() => {
		const loadData = async () => {
			setLoading(true);
			const [photos, settings, pkgs] = await Promise.all([
				fetchPhotos(),
				fetchSiteSettings(),
				fetchPackages()
			]);
			setGalleryData(photos);
			setSiteSettings(settings);
			setPackages(pkgs);
			setLoading(false);
		};

		loadData();
	}, []);

	const getFilteredImages = () => {
		if (activeCategory === 'all') {
			return Object.values(galleryData).flat();
		}
		return galleryData[activeCategory] || [];
	};

	const filteredImages = getFilteredImages();

	// Navigate images in lightbox
	const nextImage = useCallback(() => {
		if (selectedImageIndex < filteredImages.length - 1) {
			const newIndex = selectedImageIndex + 1;
			setSelectedImageIndex(newIndex);
			setSelectedImage(filteredImages[newIndex]);
		}
	}, [selectedImageIndex, filteredImages]);

	const prevImage = useCallback(() => {
		if (selectedImageIndex > 0) {
			const newIndex = selectedImageIndex - 1;
			setSelectedImageIndex(newIndex);
			setSelectedImage(filteredImages[newIndex]);
		}
	}, [selectedImageIndex, filteredImages]);

	// Swipe handlers for mobile
	const swipeHandlers = useSwipeable({
		onSwipedLeft: () => nextImage(),
		onSwipedRight: () => prevImage(),
		trackMouse: false
	});

	// Keyboard navigation
	useEffect(() => {
		const handleKeyPress = (e) => {
			if (!selectedImage) return;
			if (e.key === 'ArrowRight') nextImage();
			if (e.key === 'ArrowLeft') prevImage();
			if (e.key === 'Escape') setSelectedImage(null);
		};

		window.addEventListener('keydown', handleKeyPress);
		return () => window.removeEventListener('keydown', handleKeyPress);
	}, [selectedImage, selectedImageIndex, nextImage, prevImage]);

	const openLightbox = (image, index) => {
		setSelectedImage(image);
		setSelectedImageIndex(index);
	};

	const handleFormChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleFormSubmit = async (e) => {
		e.preventDefault();
		setFormStatus('sending');
		try {
			await emailjs.send(
				EMAILJS_SERVICE_ID,
				EMAILJS_TEMPLATE_ID,
				{
					name: formData.name,
					email: formData.email,
					service: formData.service,
					message: formData.message,
				},
				EMAILJS_PUBLIC_KEY
			);
			setFormStatus('success');
			setFormData({ name: '', email: '', service: '', message: '' });
		} catch (err) {
			console.error('EmailJS error:', err);
			setFormStatus('error');
		}
	};

	return (
		<HelmetProvider>
			<div className="App">
				<SEO />

				{/* Navigation */}
				<nav className="navbar">
					<div className="nav-container">
						<div className="logo">
							<span className="logo-text">DD</span>
							<span className="logo-full">Daniel Duarte</span>
						</div>

						<button
							className="menu-toggle"
							onClick={() => setIsMenuOpen(!isMenuOpen)}
							aria-label="Toggle menu"
						>
							{isMenuOpen ? '✕' : '☰'}
						</button>

						<ul className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
							<li><a href="#home" onClick={() => setIsMenuOpen(false)}>Home</a></li>
							<li><a href="#portfolio" onClick={() => setIsMenuOpen(false)}>Portfolio</a></li>
							<li><a href="#about" onClick={() => setIsMenuOpen(false)}>About</a></li>
							<li><a href="#packages" onClick={() => setIsMenuOpen(false)}>Packages</a></li>
							<li><a href="#contact" onClick={() => setIsMenuOpen(false)}>Contact</a></li>
						</ul>
					</div>
				</nav>

				{/* Hero Section */}
				<section id="home" className="hero">
					<div className="hero-overlay"></div>
					<div className="hero-content">
						<img 
							src="/DD-Custom-Design.jpg" 
							alt="Daniel Duarte Photography" 
							className="hero-logo" 
						/>
						<p className="hero-slogan">Capturing Moments, Elevating Brands</p>
						<a href="#portfolio" className="cta-button">View My Work</a>
					</div>
				</section>

				{/* Portfolio Section */}
				<section id="portfolio" className="portfolio">
					<div className="container">
						<h2 className="section-title">Portfolio</h2>

						{loading && (
							<div className="loading">
								<p>Loading beautiful photos...</p>
							</div>
						)}

						{!loading && (
							<>
								{/* Category Filter */}
								<div className="category-filter">
									{categories.map(cat => (
										<button
											key={cat.id}
											className={`category-btn ${activeCategory === cat.id ? 'active' : ''}`}
											onClick={() => setActiveCategory(cat.id)}
											aria-label={`View ${cat.name} photography`}
										>
											<span className="category-icon" aria-hidden="true">{cat.icon}</span>
											{cat.name}
										</button>
									))}
								</div>

								{/* Gallery Grid */}
								<div className="gallery-grid">
									{filteredImages.length === 0 && (
										<p className="no-photos">
											No photos in this category yet. Check back soon!
										</p>
									)}

									{filteredImages.map((image, index) => (
										<div
											key={`${image.id}-${index}`}
											className="gallery-item"
											onClick={() => openLightbox(image, index)}
											role="button"
											tabIndex={0}
											onKeyPress={(e) => {
												if (e.key === 'Enter') openLightbox(image, index);
											}}
										>
											<LazyLoadImage
												src={image.src}
												alt={image.alt}
												effect="blur"
												width="100%"
												height="100%"
											/>
											<div className="gallery-overlay">
												<span>View</span>
											</div>
										</div>
									))}
								</div>
							</>
						)}
					</div>
				</section>

				{/* About Section */}
				<section id="about" className="about">
					<div className="container">
						<div className="about-content">
							<div className="about-image">
								<img
									src={siteSettings.profileImage?.fields?.file?.url
										? `https:${siteSettings.profileImage.fields.file.url}`
										: '/images/daniel-profile.jpg'}
									alt="Daniel Duarte - Professional Photographer"
									loading="lazy"
								/>
							</div>
							<div className="about-text">
								<h2>About Daniel</h2>
								<p>
									{siteSettings.aboutText ||
										"Based in Massachusetts, I specialize in capturing the moments that matter most. From intimate family portraits to vibrant food photography, my goal is to tell your story through beautiful, authentic images."}
								</p>

							</div>
						</div>
					</div>
				</section>

				{/* Services Section */}
				<section className="services">
					<div className="container">
						<h2 className="section-title">Services</h2>
						<div className="services-grid">
							<div className="service-card">

								<h3>Food Photography</h3>
								<p>Restaurant menus, culinary branding, and mouth-watering dish presentations</p>
							</div>
							<div className="service-card">

								<h3>Senior Portraits</h3>
								<p>Celebrate your graduation milestone with stunning, professional portraits</p>
							</div>
							<div className="service-card">

								<h3>Maternity & Family</h3>
								<p>Capture the beauty of motherhood and cherished family moments</p>
							</div>
							<div className="service-card">

								<h3>Birthday Sessions</h3>
								<p>Make your special day unforgettable with creative birthday photography</p>
							</div>
							<div className="service-card">

								<h3>Self-Love Portraits</h3>
								<p>Empowering portrait sessions celebrating confidence and self-expression</p>
							</div>
							<div className="service-card">

								<h3>Couples Photography</h3>
								<p>Romantic and authentic couples sessions celebrating your unique love story</p>
							</div>
							<div className="service-card">

								<h3>Branding Photography</h3>
								<p>Professional headshots and branding images for businesses and entrepreneurs</p>
							</div>
						</div>
					</div>
				</section>

				{/* Packages Section */}
				<section id="packages" className="packages">
					<div className="container">
						<h2 className="section-title">Packages & Pricing</h2>
						<p className="packages-subtitle">Choose the perfect package for your needs</p>

						{packages.length === 0 && !loading && (
							<p className="no-packages">No packages available yet. Check back soon!</p>
						)}

						<div className="packages-grid">
							{packages.map((pkg) => (
								<div
									key={pkg.id}
									className={`package-card ${pkg.featured ? 'featured' : ''}`}
								>
									{pkg.featured && (
										<div className="package-badge">Most Popular</div>
									)}

									<div className="package-header">
										<h3>{pkg.packageName}</h3>
										<div className="package-price">{pkg.price}</div>
									</div>

									<p className="package-description">{pkg.description}</p>

									<ul className="package-features">
										{pkg.features.map((feature, index) => (
											<li key={index}>
												<span className="feature-icon">✓</span>
												{feature}
											</li>
										))}
									</ul>

									<a href="#contact" className="package-button">
										Book Now
									</a>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* Contact Section */}
				<section id="contact" className="contact">
					<div className="container">
						<h2 className="section-title">Let's Work Together</h2>
						<p className="contact-subtitle">Ready to capture your moments?</p>

						<div className="contact-content">
							<div className="contact-info">
								<div className="contact-item">
									<span className="contact-icon" aria-hidden="true">📍</span>
									<div>
										<h3>Location</h3>
										<p>Massachusetts</p>
									</div>
								</div>

								<div className="contact-item">
									<span className="contact-icon" aria-hidden="true">📧</span>
									<div>
										<h3>Email</h3>
										<p>
											<a href={`mailto:${siteSettings.email || 'contact@danielduarte.com'}`}>
												{siteSettings.email || 'contact@danielduarte.com'}
											</a>
										</p>
									</div>
								</div>
								<div className="social-links">
									<a
										href={siteSettings.instagram || "https://www.instagram.com/danielduartephotography"}
										target="_blank"
										rel="noopener noreferrer"
										className="social-btn"
										aria-label="Follow on Instagram"
									>
										📷 Instagram
									</a>
								</div>
							</div>

							<form className="contact-form" onSubmit={handleFormSubmit}>
								<input
									type="text"
									name="name"
									placeholder="Your Name"
									value={formData.name}
									onChange={handleFormChange}
									required
									aria-label="Your name"
								/>
								<input
									type="email"
									name="email"
									placeholder="Your Email"
									value={formData.email}
									onChange={handleFormChange}
									required
									aria-label="Your email"
								/>
								<select
									name="service"
									value={formData.service}
									onChange={handleFormChange}
									required
									aria-label="Service interested in"
								>
									<option value="">Service Interested In</option>
									<option value="food">Food Photography</option>
									<option value="seniors">Senior Portraits</option>
									<option value="maternity">Maternity/Family</option>
									<option value="birthdays">Birthday Session</option>
									<option value="selflove">Self-Love Portraits</option>
									<option value="couples">Couples Photography</option>
									<option value="branding">Branding Photography</option>
								</select>
								<textarea
									name="message"
									placeholder="Tell me about your project..."
									rows="5"
									value={formData.message}
									onChange={handleFormChange}
									required
									aria-label="Project details"
								></textarea>
								<button type="submit" className="submit-btn" disabled={formStatus === 'sending'}>
									{formStatus === 'sending' ? 'Sending...' : 'Send Message'}
								</button>
								{formStatus === 'success' && (
									<p className="form-success">✅ Message sent! Daniel will be in touch soon.</p>
								)}
								{formStatus === 'error' && (
									<p className="form-error">❌ Something went wrong. Please try again or email directly.</p>
								)}
							</form>
						</div>
					</div>
				</section>

				{/* Footer */}
				<footer className="footer">
					<div className="container">
						<p>&copy; 2026 Daniel Duarte Photography. All rights reserved.</p>
						<p className="footer-credit">
							Website by <a href="https://gonzagatech.com" target="_blank" rel="noopener noreferrer">GonzagaTech</a>
						</p>
					</div>
				</footer>

				{/* Image Modal/Lightbox */}
				{selectedImage && (
					<div
						className="modal"
						onClick={() => setSelectedImage(null)}
						role="dialog"
						aria-modal="true"
						aria-label="Image lightbox"
					>
						<div className="modal-content" {...swipeHandlers}>
							<img
								src={selectedImage.src}
								alt={selectedImage.alt}
								onClick={(e) => e.stopPropagation()}
							/>

							{selectedImageIndex > 0 && (
								<button
									className="modal-prev"
									onClick={(e) => { e.stopPropagation(); prevImage(); }}
									aria-label="Previous image"
								>
									‹
								</button>
							)}

							{selectedImageIndex < filteredImages.length - 1 && (
								<button
									className="modal-next"
									onClick={(e) => { e.stopPropagation(); nextImage(); }}
									aria-label="Next image"
								>
									›
								</button>
							)}

							<button
								className="modal-close"
								onClick={() => setSelectedImage(null)}
								aria-label="Close lightbox"
							>
								✕
							</button>

							<div className="modal-counter">
								{selectedImageIndex + 1} / {filteredImages.length}
							</div>
						</div>
					</div>
				)}
			</div>
		</HelmetProvider>
	);
}

export default App; //aZ ^zsH
